import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Link,
  Radio,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { ContractAcceptationType, Insurer, SelectionType } from 'BrokerTsLib/src/enums'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { Link as RouterLink } from 'react-router-dom'

import { InputGroup } from '@/components/InputGroup'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedOffersAddonContext } from '@/context/SelectedOffersAddonProvider'
import { Apps } from '@/dtos'
import { useSelections, useUrl } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useGetPossibleCreateContractUsers } from '@/hooks/useUser'
import { formatDateTime } from '@/utils/formatDate'

import { Apis } from '@/appSettings'
import { usePartner } from '@/context/PartnerContext'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { CheckboxFormControl, InputFormControl } from 'BrokerTsLib/src/components'
import { useEffect } from 'react'
import type { Step4FormValues } from '../Step4'
import { PreviousContractNumberForm } from './PreviousContractNumberForm'

export const CreateContractForm = () => {
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas

  const { currentPartner } = usePartner()
  const { data: dataCat } = useSelections(SelectionType.FormaSjednani)
  const { data: dataFirstPaymentType } = useSelections(
    SelectionType.ZpusobPrvniPlatby,
  )
  const { data: dataDigitalSignatureType } = useSelections(
    SelectionType.ZpusobDigitalnihoPodpisu,
  )
  const { data: dataPossibleCreateContractUsers } = useGetPossibleCreateContractUsers({ enabled: !isPublicPoleas })
  const { offer } = useSelectedQuote()

  const {
    register,
    watch,
    formState: { errors },
    setValue,
    getValues,
    control,
    trigger,
  } = useFormContext<Step4FormValues>()
  const meetingRecord = getValues('meetingRecord')
  const { fields } = useFieldArray({
    control,
    name: 'meetingRecord.brokerNumbers',
  })
  const actStartType = watch('meetingRecord.startType')

  const { addonOffers, isLoadingData } = useSelectedOffersAddonContext()
  const { isDisabled, insuranceParams, isContracted, insuranceParamsId } = useInsuranceParams()

  useEffect(() => {
    if (isPublicPoleas)
      setValue('meetingRecord.startType', ContractAcceptationType.Payment)
  }, [isPublicPoleas])

  const createdBy = insuranceParams?.generalParams.createdBy
  const contractCreatedBy = insuranceParams?.generalParams.contractCreatedBy

  let enabledAcceptTypesCount = 0
  if (meetingRecord?.paymentEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.signatureEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.digitalSignatureEnabled) enabledAcceptTypesCount++

  let vppLink
  let vppWeb
  if (isPublicPoleas) {
    switch (offer?.insurerId) {
      case Insurer.Uniqa:
        vppLink = 'https://www.uniqa.cz/dokumenty-ke-stazeni/'
        vppWeb = 'www.uniqa.cz'
        break
      case Insurer.Kooperativa:
        vppLink = 'https://www.koop.cz/dokumenty-ke-stazeni/dokumenty-k-pojisteni-vozidel'
        vppWeb = 'www.koop.cz'
        break
      case Insurer.Allianz:
        vppLink = 'https://www.allianz.cz/pro-klienty/dokumenty-a-formulare/'
        vppWeb = 'www.allianz.cz'
        break
      case Insurer.Generali:
        vppLink = 'https://www.generali.cz/pojisteni/pojisteni-motorovych-vozidel/povinne-ruceni#ke-stazeni'
        vppWeb = 'www.generali.cz'
        break
      case Insurer.GeneraliCeskaPojistovna:
        vppLink = 'https://www.generaliceska.cz/dokumenty-podle-produktu#/Pojisteni_motorovych_vozidel/NON_LIFE'
        vppWeb = 'www.generaliceska.cz'
        break
    }
  }

  const url = useUrl(Apis.preContractDocument)
  const preContractDocumentUrl = `${url}/GetPreContractDocumentPdf?paramsId=${insuranceParamsId ?? ''}`

  const showPlaceOfContract =
    isPublicPoleas &&
    [Insurer.GeneraliCeskaPojistovna, Insurer.CPP].includes(
      offer?.insurerId ?? 0,
    ) ||
    ((offer?.insurerId ?? 0) == Insurer.Uniqa &&
      currentPartner!.partner != 'Jap') //JAP-1502



  if (!dataCat) return null

  return (
    <FormBody>
      <FormRow>
        {!isPublicPoleas &&
          <FormControl isInvalid={!!errors.meetingRecord?.startType?.message}>
            <FormLabel>
              Způsob sjednání
              {isLoadingData && <Spinner ml={2} />}
            </FormLabel>

            {dataCat?.data && (
              <RadioGroup
                {...register('meetingRecord.startType')}
                valueTransform={(value) => value?.toString()}
                onChange={(e) => {
                  setValue('meetingRecord.startType', parseInt(e))
                  trigger('meetingRecord.startType')
                }}
                isDisabled={enabledAcceptTypesCount < 2 || isDisabled}
                stackProps={{
                  direction: 'column',
                  spacing: 4,
                }}
              >
                {dataCat?.data
                  .sort(
                    (it1, it2) =>
                      (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                  )
                  .map((item) => (
                    <Radio
                      value={item.value}
                      key={item.id}
                      isDisabled={
                        (addonOffers.length > 0 &&
                          Number(item.value) ==
                          ContractAcceptationType.Signature) ||
                        isDisabled ||
                        isLoadingData ||
                        (!meetingRecord?.paymentEnabled &&
                          Number(item.value) ==
                          ContractAcceptationType.Payment) ||
                        (!meetingRecord?.signatureEnabled &&
                          Number(item.value) ==
                          ContractAcceptationType.Signature) ||
                        (!meetingRecord?.digitalSignatureEnabled &&
                          Number(item.value) ==
                          ContractAcceptationType.DigitalSignature)
                      }
                    >
                      {item.description}
                    </Radio>
                  ))}
              </RadioGroup>
            )}
          </FormControl>
        }

        {(meetingRecord?.firstPaymentTypePaymentGateEnabled ||
          meetingRecord?.firstPaymentTypeBankTransferEnabled) &&
          actStartType == ContractAcceptationType.Payment && (
          <FormControl
            isInvalid={!!errors.meetingRecord?.firstPaymentType?.message}
          >
            <FormLabel>Způsob první platby</FormLabel>

            {dataFirstPaymentType?.data && (
              <RadioGroup
                {...register('meetingRecord.firstPaymentType')}
                valueTransform={(value) => value?.toString()}
                onChange={(e) => {
                  setValue('meetingRecord.firstPaymentType', parseInt(e))
                  trigger('meetingRecord.firstPaymentType')
                }}
                stackProps={{
                  direction: 'column',
                  spacing: 4,
                }}
              >
                {dataFirstPaymentType?.data
                  .sort(
                    (it1, it2) =>
                      (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                  )
                  .map((item) => (
                    <Radio value={item.id.toString()} key={item.id} isDisabled={(item.name === 'PaymentGate' && !meetingRecord.firstPaymentTypePaymentGateEnabled) || (item.name === 'Bank' && !meetingRecord.firstPaymentTypeBankTransferEnabled) || isDisabled}>
                      {item.description}
                    </Radio>
                  ))}
              </RadioGroup>
            )}

            {errors.meetingRecord?.firstPaymentType && <FormErrorMessage>{errors.meetingRecord.firstPaymentType.message}</FormErrorMessage>}
          </FormControl>
        )}

        {dataDigitalSignatureType?.data &&
          dataDigitalSignatureType.data.length > 0 &&
          actStartType == ContractAcceptationType.DigitalSignature && (
          <FormControl
            isInvalid={!!errors.meetingRecord?.digitalSignatureType?.message}
          >
            <FormLabel>Způsob digitálního podpisu</FormLabel>

            {dataDigitalSignatureType?.data && (
              <RadioGroup
                {...register('meetingRecord.digitalSignatureType')}
                valueTransform={(value) => value?.toString()}
                onChange={(e) => {
                  setValue('meetingRecord.digitalSignatureType', parseInt(e))
                  trigger('meetingRecord.digitalSignatureType')
                }}
                isDisabled={isDisabled}
                stackProps={{
                  direction: 'column',
                  spacing: 4,
                }}
              >
                {dataDigitalSignatureType?.data
                  .filter((it) => (meetingRecord.allowedDigitalSignatureTypes?.indexOf(it.id) ?? -1) != -1)
                  .sort(
                    (it1, it2) =>
                      (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                  )
                  .map((item) => (
                    <Radio value={item.id.toString()} key={item.id}>
                      {item.description}
                    </Radio>
                  ))}
              </RadioGroup>
            )}
          </FormControl>
        )}

      </FormRow>

      {showPlaceOfContract && (
        <FormRow>
          <InputFormControl
            label="Místo sjednání"
            name="meetingRecord.placeOfContract"
            control={control}
            isDisabled={isDisabled}
          />
        </FormRow>
      )}

      <PreviousContractNumberForm></PreviousContractNumberForm>

      {(app != Apps.extra && !isPublicPoleas || dataPossibleCreateContractUsers?.data && !isContracted) &&
        <FormRow>
          {app != Apps.extra && !isPublicPoleas && (
            <FormControl
              isInvalid={!!errors.meetingRecord?.brokerNumbers?.message}
            >
              <FormLabel>Získatelské číslo</FormLabel>

              {fields.map((field, index) => (
                <InputGroup
                  key={field.acquisitionNumber}
                  {...register(
                    `meetingRecord.brokerNumbers.${index}.acquisitionNumber`,
                  )}
                  placeholder="Např. 12348765"
                  isDisabled
                />
              ))}
            </FormControl>
          )}

          {dataPossibleCreateContractUsers?.data && !isContracted && (
            <FormControl
              isInvalid={!!errors.meetingRecord?.createContractAsUserId?.message}
            >
              <FormLabel>Sjednat pojištění jako</FormLabel>

              <Select
                {...register('meetingRecord.createContractAsUserId')}
                isDisabled={isDisabled}
              >
                {dataPossibleCreateContractUsers.data.map((item) => (
                  <option value={item.userId} key={item.userId}>
                    {`${item.surname} ${item.name}`} ({item.ouName})
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
        </FormRow>
      }
      {isPublicPoleas &&
        <FormRow itemsCount={1} sx={{ rowGap: 4 }}>
          <CheckboxFormControl
            control={control}
            isDisabled={isDisabled}
            name={'meetingRecord.preContractDocumentAgreement'}
            label={<Text>Souhlasím s informacemi v předsmluvním <Link target='_blank' variant='underline' href={preContractDocumentUrl}>dokumentu</Link>
            </Text>}
          />

          <CheckboxFormControl
            control={control}
            isDisabled={isDisabled}
            name={'meetingRecord.vppAgreement'}
            label={<Text>Souhlasím se všeobecnými pojistnými podmínkami na stránkách <Link target='_blank' variant='underline' href={vppLink}>{vppWeb}</Link></Text>}
          />
        </FormRow>
      }

      {isContracted && !isPublicPoleas && (
        <>
          {createdBy && (
            <FormControl>
              <FormLabel>Nabídku zpracoval/a</FormLabel>

              <Text>
                {formatDateTime(insuranceParams!.generalParams.created!)},{' '}
                <Link
                  as={RouterLink}
                  colorScheme="blue"
                  target="_blank"
                  to={`/user/${createdBy.userId}`}
                >
                  {createdBy.name} {createdBy.surname}
                </Link>
                <Link
                  as={RouterLink}
                  colorScheme="blue"
                  target="_blank"
                  to={`/organization/${createdBy.ouId}`}
                >
                  {createdBy.ouName && ` (${createdBy.ouName})`}
                </Link>
                ,{' '}
                <Link
                  colorScheme="blue"
                  target="_blank"
                  href={`mailto:${createdBy.email}`}
                >
                  {createdBy.email && createdBy.email}
                </Link>
              </Text>
            </FormControl>
          )}

          {contractCreatedBy && (
            <FormControl>
              <FormLabel>Smlouvu sjednal/a</FormLabel>

              <Text>
                {formatDateTime(
                  insuranceParams!.generalParams.contractCreatedDate!,
                )}
                ,{' '}
                <Link
                  as={RouterLink}
                  colorScheme="blue"
                  target="_blank"
                  to={`/user/${contractCreatedBy.userId}`}
                >
                  {contractCreatedBy.name} {contractCreatedBy.surname}
                </Link>
                <Link
                  as={RouterLink}
                  colorScheme="blue"
                  target="_blank"
                  to={`/organization/${contractCreatedBy.ouId}`}
                >
                  {contractCreatedBy.ouName && ` (${contractCreatedBy.ouName})`}
                </Link>
                ,{' '}
                <Link
                  as={'a'}
                  colorScheme="blue"
                  target="_blank"
                  href={`mailto:${contractCreatedBy.email}`}
                >
                  {contractCreatedBy.email && contractCreatedBy.email}
                </Link>
              </Text>
            </FormControl>
          )}
        </>
      )}
    </FormBody>
  )
}